import { Col, Form, FormInstance, Input, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OrganizationSelect from "../kits/kits-transfer-form/OrganizationSelect";
import {
  AppDispatch,
  dynamicSet,
  getChildOrganizationList,
  getChildOrganizationListClear,
  getChildOrganizationListRequest,
  getLevel,
  getLevelClear,
  getLevelRequest,
  getMyOrganization,
  getMyOrganizationRequest,
  getOrganization,
  getOrganizationRequest,
  getParentHierarchy,
  Organization,
  useDynamicSelector,
} from "@src/redux";
import { useDispatch, useSelector } from "react-redux";
import DynamicSelect from "../kits/kits-transfer-form/DynamicSelect";
import { isArray } from "lodash";
import store from "@src/redux/store";

interface DynamicTreeSelectProps {
  form: FormInstance;
  col?: number;
  mutationType?: string;
  kitAssignCheck?: boolean;
  isRequired: boolean;
  allowClear: boolean;
  className?: string;
  showLevelAttribute?: boolean;
  organizationId?: string;
  key?: string;
  showAllLabel?: boolean;
  fetchRequest?: (id?: string) => void;
}

const DynamicTreeSelect: React.FC<DynamicTreeSelectProps> = (props) => {
  const {
    form,
    col,
    mutationType,
    kitAssignCheck = false,
    isRequired,
    allowClear,
    className,
    showLevelAttribute = false,
    organizationId,
    key = "form",
    showAllLabel = true,
    fetchRequest,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [childListItems, setChildListItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [labels, setLabels] = useState<any>([]);
  const organizationSelectIds = Form.useWatch("organizationId", form);
  const levelAttributeId = Form.useWatch("levelAttributeId", form);
  const { levelName } = useDynamicSelector("level_name");
  const [childOrganization, setChildOrganization] = useState<
    Organization[] | null
  >([]);
  const [levelDetails, setLevelDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState<Boolean>(false);
  const [lastSelectionIndex, setLastSelectionIndex] = useState<number>(-1);
  const [lastSelectedParentId, setLastSelectedParentId] = useState<
    string | null
  >(null);
  const [myOrganizationResult, setMyOrganizationResult] =
    useState<Organization | null>();
  const [isClearingSelection, setIsClearingSelection] =
    useState<boolean>(false);

  const { result: getSubjectOrganization } =
    useSelector(getParentHierarchy);

  const handleGetMyOrganization = async () => {
    setLoading(true);
    await dispatch(getMyOrganizationRequest({})).unwrap();
    const state = store.getState();
    let getOrg = await getMyOrganization(state);
    setLoading(false);
    setMyOrganizationResult(getOrg?.result);
    try {
      const list: any[] =
        getOrg?.result?.rootOrganization?.rootLevelList || [];
      // A user positioned at a leaf level has no sub-levels to drill into.
      // Building a level-filter dropdown for them fetches (nonexistent) child
      // organizations and renders an empty "No data" select. Skip the filter
      // entirely so such users simply see their own scope (fixes the
      // "Project section shows No data" report for leaf-level clinical admins,
      // common in shallow-hierarchy clients like the V1-migrated LTHE).
      // Only act when we have a fully-resolved level object — getMyOrganization
      // is dispatched by several components at once and is briefly null while
      // pending; clearing on that transient state would wrongly drop the filter
      // for non-leaf users too.
      const userLevel = getOrg?.result?.level;
      const isLeafUser =
        !!userLevel && (userLevel.childLevelList?.length || 0) === 0;
      if (isLeafUser) {
        setLabels([]);
        setChildListItems([]);
      } else if (Array.isArray(list) && list.length > 0) {
        const root: any | undefined = list.find((l: any) => !l.parentLevelId);
        const chainNames: string[] = [];
        let current: any | undefined = root;
        while (current) {
          chainNames.push(current.name as string);
          const next: any | undefined = list.find(
            (l: any) => l.parentLevelId === (current as any).id
          );
          current = next;
        }
        const ordered = chainNames.slice(1);
        const currentLevelName: string | undefined =
          getOrg?.result?.level?.name;
        const startIdx = currentLevelName
          ? ordered.indexOf(currentLevelName)
          : -1;
        const effective = startIdx >= 0 ? ordered.slice(startIdx) : ordered;
        setLabels(effective);
        const firstLabel: string | undefined =
          effective.length > 0 ? effective[0] : undefined;
        const showTopOrg = !className;
        const isUserAtFirstLabel = !!(
          firstLabel && getOrg?.result?.level?.name === firstLabel
        );
        const shouldSkipFirstDynamic = showTopOrg && isUserAtFirstLabel;
        setChildListItems(
          shouldSkipFirstDynamic ? [] : firstLabel ? [{ [firstLabel]: [] }] : []
        );
      }
    } catch (e) {
      // noop
    }
    return getOrg.result;
  };

  const handleGetOrganization = async () => {
    setLoading(true);
    await dispatch(
      getOrganizationRequest({ filter: { id: organizationId } })
    ).unwrap();

    const state = store.getState();
    let getOrg = await getOrganization(state);
    setLoading(false);

    return getOrg.result;
  };

  const fetchChildLevelList = async (
    _id?: any,
    levelAttributeIdList?: { id: string; value: string }[]
  ) => {
    setLoading(true);
    await dispatch(
      getChildOrganizationListRequest({
        filter: {
          parentOrganizationId: _id ? _id : organizationId,
          levelAttributeValueList: levelAttributeIdList || undefined,
        },
        pagination: {
          pageLimit: 1000,
          pageNumber: 1,
        },
        sort: [
          {
            field: "name",
            order: "asc",
          },
        ],
      })
    ).unwrap();
    await fetchRequest?.(_id);

    const state = store.getState();
    let childOrganizationList = await getChildOrganizationList(state);
    setLoading(false);
    setChildOrganization(
      childOrganizationList?.result?.items &&
        childOrganizationList?.result?.items?.length > 0
        ? childOrganizationList?.result?.items
        : []
    );

    const apiLevel = childOrganizationList?.result?.items?.[0]?.level || null;
    if (apiLevel) {
      setLevelDetails(apiLevel);
    }
  };

  const getNextLevelNameByLevelId = (levelId?: string) => {
    if (!levelId) return undefined;
    const list =
      myOrganizationResult?.rootOrganization?.rootLevelList || [];
    const found = list.find((lvl: any) => lvl?.id === levelId);
    return found?.childLevelList?.[0]?.name;
  };

  const fetchLevelData = async (_id: any, name: any = undefined) => {
    setLoading(true);
    await dispatch(
      getLevelRequest({
        filter: {
          id: _id,
          name: name,
        },
      })
    );
    const state = store.getState();
    let levelDetails = await getLevel(state);
    setLoading(false);
    setLevelDetails(levelDetails?.result);
  };

  const handleChange = (
    _id: string,
    label: string,
    organization: any,
    index: number
  ) => {
    // Skip if clearing
    if (isClearingSelection) {
      return;
    }

    // Skip if no valid ID
    if (!_id) {
      return;
    }

    setLastSelectionIndex(index);
    setLastSelectedParentId(_id);

    // Update form with selected value at this index, removing any values after this index
    const newOrgIds = Array.isArray(organizationSelectIds)
      ? organizationSelectIds.slice(0, index)
      : [];
    newOrgIds[index] = _id;

    form.setFieldsValue({
      organizationId: newOrgIds,
    });

    form.setFieldsValue({
      levelAttributeId: [],
    });

    setSelectedIds((prev) => {
      const updatedSelectedIds = [...prev];
      updatedSelectedIds[index] = _id;
      return updatedSelectedIds.slice(0, index + 1);
    });

    setChildListItems((prev) => {
      const updatedChildListItems = [...prev];
      updatedChildListItems[index] = {
        [label]: updatedChildListItems[index]?.[label],
      };
      return updatedChildListItems.slice(0, index + 1);
    });

    // Fetch children based on selected parent id
    fetchChildLevelList(_id);
  };

  const handleChangeLevelAttributes = (
    _id: string,
    label: string,
    organization: any,
    index: number
  ) => {
    setLastSelectionIndex(index);
    setLastSelectedParentId(_id);
    dispatch(dynamicSet("level_name", { levelName: label }));
    form.setFieldsValue({
      organizationId: { [index + 1]: null },
    });

    let organizationId =
      organizationSelectIds?.[organizationSelectIds?.length - 1] ||
      myOrganizationResult?.id;

    setSelectedIds((prev) => {
      const updatedSelectedIds = [...prev];
      updatedSelectedIds[index] = _id;

      return updatedSelectedIds.slice(0, index + 1);
    });

    setChildListItems((prev) => {
      const updatedChildListItems = [...prev];
      updatedChildListItems[index] = {
        [label]: updatedChildListItems[index]?.[label] || null,
      };
      return updatedChildListItems.slice(0, index + 1);
    });

    if (organizationId) {
      fetchChildLevelList(
        organizationId,
        levelAttributeId?.filter((list) => list?.value)
      );
    }
  };

  const findLevelAttributeList = (data: any[], targetId: string) => {
    for (const item of data) {
      for (const key in item) {
        const levels = item[key];
        for (const levelObj of levels) {
          if (levelObj.id === targetId) {
            const levelAttributes = levelObj?.level?.levelAttributeList;
            return { [levelObj?.level?.name]: levelAttributes };
          }
        }
      }
    }
    return {};
  };

  const handleClearSelection = async (index: number) => {
    setIsClearingSelection(true);

    try {
      let parentId: string | null = null;

      if (index === 0) {
        form.setFieldsValue({ organizationId: [] });
        setSelectedIds([]);
        setChildListItems([]);
        setLastSelectionIndex(-1);
        setLastSelectedParentId(null);

        const result = await (organizationId
          ? handleGetOrganization()
          : handleGetMyOrganization());

        const rootId = organizationId || result?.id;

        if (rootId && mutationType === "add") {
          setSelectedIds([rootId]);
          await fetchChildLevelList(rootId);
        }

        return; 
      } else {
        parentId = Array.isArray(organizationSelectIds)
          ? organizationSelectIds[index - 1]
          : null;
      }

      const updatedOrgIds = Array.isArray(organizationSelectIds)
        ? organizationSelectIds.slice(0, index)
        : [];

      form.setFieldsValue({
        organizationId: updatedOrgIds,
      });

      setSelectedIds((prev) => prev.slice(0, index));
      setChildListItems((prev) => prev.slice(0, index));
      setLastSelectionIndex(index - 1);
      setLastSelectedParentId(parentId);

      if (parentId) {
        await fetchChildLevelList(parentId);
      }
    } catch (error) {
      console.error("Error clearing selection:", error);
    } finally {
      setIsClearingSelection(false);
    }
  };

  useEffect(() => {
    if (mutationType === "edit") setChildListItems([]);
  }, []);

  useEffect(() => {
    if (childOrganization !== null && selectedIds?.length > 0) {
      const viewingHasTopOrg = !className;
      const userCurrentLevelName: string | undefined =
        myOrganizationResult?.level?.name;
      let currentIndex = lastSelectionIndex;
      const skipFirstDynamic =
        viewingHasTopOrg &&
        !!labels?.[0] &&
        !!userCurrentLevelName &&
        labels[0] === userCurrentLevelName;
      if (currentIndex < 0 && skipFirstDynamic) {
        currentIndex = -1;
      }
      const nextIndex = currentIndex + 1;
      const labelIndex = nextIndex + (skipFirstDynamic ? 1 : 0);
      const nextLevelName = labels?.[labelIndex] || "";
      const newOptions = (childOrganization || []).map((c: any) => ({
        id: c.id,
        name: c.shortName || c.name,
        level: c.level,
        parentOrganizationId: c.parentOrganizationId,
      }));

      if (kitAssignCheck && levelDetails?.allowKitAssign === true) {
        setLevelDetails(null);
        setChildListItems((prev) => {
          const clone = [...prev];
          const upto = clone.slice(0, nextIndex);
          if (nextLevelName) upto[nextIndex] = { [nextLevelName]: newOptions };
          return upto;
        });
        if (!labels.includes(nextLevelName)) {
          setLabels([...labels, nextLevelName]);
        }
        setChildOrganization(null);
      } else if (!kitAssignCheck) {
        setChildListItems((prev) => {
          const clone = [...prev];
          const upto = clone.slice(0, nextIndex);
          if (nextLevelName) {
            upto[nextIndex] = { [nextLevelName]: newOptions };
          }
          return upto;
        });
        if (!labels.includes(nextLevelName)) {
          setLabels([...labels, nextLevelName]);
        }
        setChildOrganization(null);
      }
      dispatch(getLevelClear());
      dispatch(getChildOrganizationListClear());
    } else if (
      childOrganization &&
      childOrganization?.length > 0 &&
      levelName
    ) {
      const newOptions = (childOrganization || []).map((c: any) => ({
        id: c.id,
        name: c.shortName || c.name,
        level: c.level,
        parentOrganizationId: c.parentOrganizationId,
      }));
      setChildListItems((prevChildListItems) =>
        prevChildListItems.map((item) =>
          item.hasOwnProperty(levelName) ? { [levelName]: newOptions } : item
        )
      );
      setChildOrganization(null);
      dispatch(getChildOrganizationListClear());
    }
  }, [
    childOrganization,
    levelDetails,
    kitAssignCheck,
    selectedIds,
    levelName,
    labels,
    lastSelectionIndex,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = organizationId
          ? await handleGetOrganization()
          : await handleGetMyOrganization();
        // Leaf-level users (own level has no child levels) have nothing to
        // drill into — don't fetch child orgs, which would only populate an
        // empty "No data" dropdown.
        const canDrillDown =
          !!organizationId ||
          (result?.level?.childLevelList?.length || 0) > 0;
        if (
          (organizationId || result?.id) &&
          mutationType === "add" &&
          canDrillDown
        ) {
          const initialParentId = result?.id;
          setSelectedIds((prevSelectedIds) => [
            ...prevSelectedIds,
            initialParentId,
          ]);
          await fetchChildLevelList(initialParentId);
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
      }
    };

    fetchData();
  }, [mutationType]);

  useEffect(() => {
    if (
      getSubjectOrganization?.items &&
      getSubjectOrganization?.items?.length > 0 &&
      mutationType === "edit"
    ) {
      let reAssign = getSubjectOrganization?.items
        .map((list: any) => {
          let getOrganizationList = list.organizationList.map(
            (organization: any) => {
              let findOne =
                myOrganizationResult?.rootOrganization?.rootLevelList?.find(
                  (findLevel: any) => findLevel?.name === list?.level?.name
                );
              return {
                ...organization,
                level: {
                  id: findOne?.id,
                  name: findOne?.name,
                  childLevelList: findOne?.childLevelList,
                },
              };
            }
          );

          return {
            [list?.level?.name]: getOrganizationList,
          };
        })
        .slice(1);
      let formSetValues = getSubjectOrganization?.items
        .map((list) => list.organizationId)
        .slice(1);

      let getLabels = getSubjectOrganization?.items
        ?.map((list) => list?.level?.name)
        ?.slice(1);
      setLabels(getLabels);
      setChildListItems(reAssign);
      form.setFieldsValue({ organizationId: formSetValues });
    }
  }, [getSubjectOrganization, mutationType]);

  useEffect(() => {
    let organizationId = form.getFieldValue("organizationId");
    let getId =
      (organizationId?.length > 0 &&
        organizationId[organizationId.length - 1]) ||
      (organizationId?.length > 1 &&
        organizationId[organizationId.length - 2]);
    form.setFieldValue("my_org", getId || myOrganizationResult?.id);
  }, [form, myOrganizationResult, organizationSelectIds]);

  return (
    <>
      {!className && (
        <Col span={col || 12}>
          <OrganizationSelect
            form={form}
            className={className}
            myOrganizationResult={myOrganizationResult}
          />
        </Col>
      )}
      {childListItems?.length > 0 &&
        childListItems?.map((item, index) => {
          let getAttributesList: any = [];
          const showTopOrg = !className;
          const userLevelName: string | undefined =
            myOrganizationResult?.level?.name;
          const skipFirstDynamic =
            showTopOrg &&
            !!labels?.[0] &&
            !!userLevelName &&
            labels[0] === userLevelName;
          const keyLabel = labels?.[index + (skipFirstDynamic ? 1 : 0)];

          if (
            Array.isArray(organizationSelectIds) &&
            (organizationSelectIds.length > 0 ||
              organizationSelectIds.length > 1)
          ) {
            const selectedId =
              organizationSelectIds[organizationSelectIds.length - 1] ||
              organizationSelectIds[organizationSelectIds.length - 2];

            if (selectedId) {
              getAttributesList = findLevelAttributeList(
                childListItems,
                selectedId
              );
            }
          }

          return (
            <Col span={col || 12} key={index}>
              <DynamicSelect
                form={form}
                options={(item && keyLabel && item[keyLabel]) || []}
                handleChange={handleChange}
                handleChangeLevelAttributes={handleChangeLevelAttributes}
                index={index}
                label={keyLabel || t("Select childLevelList")}
                isRequired={isRequired}
                allowClear={allowClear}
                className={className}
                levelAttributes={getAttributesList}
                showLevelAttribute={false}
                myOrganizationResult={myOrganizationResult}
                showAllLabel={showAllLabel}
                onClear={handleClearSelection}
              />
            </Col>
          );
        })}
      {loading && (
        <Col span={col || 12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Spin />
          </div>
        </Col>
      )}
      <Form.Item
        name={"my_org"}
        initialValue={myOrganizationResult?.id}
        hidden
      >
        <Input />
      </Form.Item>
    </>
  );
};

export default DynamicTreeSelect;
